import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import {
    formatCreatedAtDate,
    formatNumberWithComma
} from '../../utils/helpers';

// Helper function to get servant name from bar_order_items
const getServantName = (servantId, servantNamesData) => {
    if (!servantId) return 'N/A';
    if (!servantNamesData || !Array.isArray(servantNamesData)) return 'N/A';
    const servant = servantNamesData.find(s => parseInt(s.id) === parseInt(servantId));
    return servant ? (servant.employee_name || servant.name) : 'N/A';
};

// Helper function to get all unique servants from order items
const getAllServantsFromOrder = (order, servantNamesData) => {
    if (!order.bar_order_items || !Array.isArray(order.bar_order_items)) {
        return getServantName(order.servant_id, servantNamesData);
    }
    
    const servantIds = [...new Set(order.bar_order_items.map(item => item.servant_id))];
    const servantNames = servantIds.map(id => getServantName(id, servantNamesData));
    return servantNames.join('; ') || 'N/A';
};

// Helper function to get item details string
const getItemDetails = (item) => {
    return `${item.stock_name} (${item.quantity} x ${formatNumberWithComma(item.price)} = ${formatNumberWithComma(item.total_amount)})`;
};

// Helper function to get payment method display name
const getPaymentMethodDisplay = (method) => {
    const paymentMethods = {
        'cash': 'Cash',
        'card': 'Card',
        'bank': 'Bank Transfer',
        'mobile': 'Mobile Money',
        'pending': 'Pending'
    };
    return paymentMethods[method] || method;
};

// Export to CSV with all attributes (using direct download)
export const exportOrdersToCSV = async (data, referenceData = {}) => {
    try {
        const { servantNamesData = [] } = referenceData;
        
        const fileName = `bar_orders_${new Date().toISOString().split('T')[0]}.csv`;
        
        const csvData = data.map(order => {
            const items = order.bar_order_items || [];
            const servantDisplay = getAllServantsFromOrder(order, servantNamesData);
            const itemDetails = items.map(item => getItemDetails(item)).join(' | ');
            
            return {
                'Order Number': order.order_number,
                'Date': formatCreatedAtDate(order.created_at),
                'Time': new Date(order.created_at).toLocaleTimeString(),
                'Customer Name': order.customer_id || 'Walk-in Customer',
                'Customer Type': order.customer_type?.name || 'N/A',
                'Servant(s)': servantDisplay,
                'Items Count': items.length,
                'Item Details': itemDetails,
                'Total Amount': formatNumberWithComma(order.total_amount),
                'Order Status': order.order_status?.toUpperCase() || 'N/A',
                'Payment Method': getPaymentMethodDisplay(order.payment_method),
                'Transaction ID': order.transaction_id || 'N/A',
                'Order Notes': order.order_notes || '',
                'Payment Notes': order.payment_notes || '',
                'Created At': new Date(order.created_at).toLocaleString()
            };
        });

        if (csvData.length === 0) {
            return Promise.resolve();
        }

        const csvHeaders = Object.keys(csvData[0]);
        const csvRows = csvData.map(row => 
            csvHeaders.map(header => {
                const value = row[header] || '';
                const escapedValue = String(value).replace(/"/g, '""');
                return escapedValue.includes(',') ? `"${escapedValue}"` : escapedValue;
            }).join(',')
        );
        
        const csvString = [csvHeaders.join(','), ...csvRows].join('\r\n');
        const BOM = '\uFEFF';
        const csvContent = BOM + csvString;
        
        // Create and download without FileSaver
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        return Promise.resolve();
    } catch (error) {
        return Promise.reject(error);
    }
};

// Export to Excel with all attributes (using direct download)
export const exportOrdersToExcel = async (data, referenceData = {}) => {
    try {
        const { servantNamesData = [] } = referenceData;
        
        const fileExtension = '.xlsx';
        const fileName = `bar_orders_${new Date().toISOString().split('T')[0]}${fileExtension}`;
        
        // Create main orders sheet
        const wsData = [
            ['Order Number', 'Date', 'Time', 'Customer Name', 'Customer Type', 'Servant(s)', 'Items Count', 'Item Details', 'Total Amount', 'Order Status', 'Payment Method', 'Transaction ID', 'Order Notes', 'Payment Notes', 'Created At'],
            ...data.map(order => {
                const items = order.bar_order_items || [];
                const servantDisplay = getAllServantsFromOrder(order, servantNamesData);
                const itemDetails = items.map(item => getItemDetails(item)).join('\n');
                
                return [
                    order.order_number,
                    formatCreatedAtDate(order.created_at),
                    new Date(order.created_at).toLocaleTimeString(),
                    order.customer_id || 'Walk-in Customer',
                    order.customer_type?.name || 'N/A',
                    servantDisplay,
                    items.length,
                    itemDetails,
                    formatNumberWithComma(order.total_amount),
                    order.order_status?.toUpperCase() || 'N/A',
                    getPaymentMethodDisplay(order.payment_method),
                    order.transaction_id || 'N/A',
                    order.order_notes || '',
                    order.payment_notes || '',
                    new Date(order.created_at).toLocaleString()
                ];
            })
        ];

        // Create items sheet with detailed item information
        const itemsSheetData = [
            ['Order Number', 'Date', 'Time', 'Customer Name', 'Item Name', 'Quantity', 'Price', 'Total', 'Servant', 'Item Time', 'Item Notes']
        ];

        data.forEach(order => {
            const items = order.bar_order_items || [];
            items.forEach(item => {
                itemsSheetData.push([
                    order.order_number,
                    formatCreatedAtDate(order.created_at),
                    new Date(order.created_at).toLocaleTimeString(),
                    order.customer_id || 'Walk-in Customer',
                    item.stock_name,
                    item.quantity,
                    formatNumberWithComma(item.price),
                    formatNumberWithComma(item.total_amount),
                    getServantName(item.servant_id, servantNamesData),
                    new Date(item.time).toLocaleTimeString(),
                    item.order_notes || ''
                ]);
            });
        });

        const wb = XLSX.utils.book_new();
        
        // Add Orders sheet
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        
        // Set column widths for Orders sheet
        const wscols = [
            { wch: 20 }, // Order Number
            { wch: 12 }, // Date
            { wch: 10 }, // Time
            { wch: 25 }, // Customer Name
            { wch: 15 }, // Customer Type
            { wch: 30 }, // Servant(s)
            { wch: 12 }, // Items Count
            { wch: 50 }, // Item Details
            { wch: 15 }, // Total Amount
            { wch: 12 }, // Order Status
            { wch: 15 }, // Payment Method
            { wch: 20 }, // Transaction ID
            { wch: 30 }, // Order Notes
            { wch: 30 }, // Payment Notes
            { wch: 20 }  // Created At
        ];
        ws['!cols'] = wscols;
        XLSX.utils.book_append_sheet(wb, ws, 'Bar Orders');

        // Add Items sheet
        const itemsWs = XLSX.utils.aoa_to_sheet(itemsSheetData);
        const itemsWscols = [
            { wch: 20 }, // Order Number
            { wch: 12 }, // Date
            { wch: 10 }, // Time
            { wch: 25 }, // Customer Name
            { wch: 25 }, // Item Name
            { wch: 10 }, // Quantity
            { wch: 15 }, // Price
            { wch: 15 }, // Total
            { wch: 20 }, // Servant
            { wch: 10 }, // Item Time
            { wch: 30 }  // Item Notes
        ];
        itemsWs['!cols'] = itemsWscols;
        XLSX.utils.book_append_sheet(wb, itemsWs, 'Order Items');

        // Use XLSX.writeFile to download directly
        XLSX.writeFile(wb, fileName);
        
        return Promise.resolve();
    } catch (error) {
        return Promise.reject(error);
    }
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = doc.getTextWidth(currentLine + ' ' + word);
        if (width < maxWidth) {
            currentLine += ' ' + word;
        } else {
            lines.push(currentLine);
            currentLine = word;
        }
    }
    lines.push(currentLine);
    return lines;
};

// Manual table drawing function for PDF
const drawOrdersTable = (doc, data, startY) => {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const rowHeight = 12;

    // Column widths for landscape mode
    const colWidths = [30, 45, 25, 30, 30, 25, 25, 30]; // Date, Order, Customer, Servant, Items Count, Total, Status, Payment

    let currentY = startY;
    const neededHeight = (data.length + 1) * rowHeight;

    // Check if we need a new page
    if (currentY + neededHeight > pageHeight - 30) {
        doc.addPage('l');
        currentY = margin;
    }

    // Draw table header
    doc.setFillColor(25, 118, 210);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');

    const headers = ['Date', 'Order #', 'Customer', 'Servant(s)', 'Items', 'Total', 'Status', 'Payment'];
    let xPos = margin;

    // Draw header background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

    // Draw header text
    headers.forEach((header, index) => {
        const cellX = xPos + 2;
        doc.text(header, cellX, currentY + 7);
        xPos += colWidths[index];
    });

    currentY += rowHeight;

    // Draw table rows
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);

    data.forEach((row, rowIndex) => {
        // Calculate needed height for this row
        let maxLines = 1;
        const cellLines = row.map((cell, cellIndex) => {
            const text = String(cell);
            const maxWidth = colWidths[cellIndex] - 4;
            const lines = splitTextIntoLines(doc, text, maxWidth);
            maxLines = Math.max(maxLines, lines.length);
            return lines;
        });

        const actualRowHeight = rowHeight * maxLines;

        // Check for page break
        if (currentY + actualRowHeight > pageHeight - 30) {
            doc.addPage('l');
            currentY = margin;
            
            // Redraw header on new page
            doc.setFillColor(25, 118, 210);
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, 'bold');
            
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            xPos = margin;
            headers.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 7);
                xPos += colWidths[index];
            });
            
            currentY += rowHeight;
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 248, 248);
        } else {
            doc.setFillColor(255, 255, 255);
        }
        
        // Draw row background
        doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
        
        // Draw cell borders
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        
        // Draw vertical lines
        let verticalX = margin;
        for (let i = 0; i <= colWidths.length; i++) {
            doc.line(verticalX, currentY, verticalX, currentY + actualRowHeight);
            if (i < colWidths.length) verticalX += colWidths[i];
        }
        
        // Draw horizontal lines
        doc.line(margin, currentY, pageWidth - margin, currentY);
        doc.line(margin, currentY + actualRowHeight, pageWidth - margin, currentY + actualRowHeight);
        
        // Draw row text with multi-line support
        cellLines.forEach((lines, cellIndex) => {
            xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
            
            lines.forEach((line, lineIndex) => {
                const lineY = currentY + 7 + (lineIndex * rowHeight);
                doc.text(line, xPos + 2, lineY);
            });
        });
        
        currentY += actualRowHeight;
    });

    return currentY;
};

// Export to PDF with specified columns only
export const exportToPDF = (data, filters = {}) => {
    return new Promise((resolve, reject) => {
        try {
            // Create PDF in landscape mode
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });
            
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 20;
            let currentY = margin;
            
            // Title
            doc.setFontSize(20);
            doc.setTextColor(25, 118, 210);
            doc.text("Bar Orders Report", pageWidth / 2, currentY, { align: 'center' });
            currentY += 15;
            
            // Date and time
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, currentY);
            currentY += 8;
            
            // Total records
            doc.setFontSize(11);
            doc.setTextColor(60, 60, 60);
            doc.text(`Total Orders: ${data.length}`, margin, currentY);
            
            const totalAmount = data.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
            const totalAmountText = `Total Amount: ${formatNumberWithComma(totalAmount)}`;
            const totalAmountWidth = doc.getTextWidth(totalAmountText);
            doc.text(totalAmountText, pageWidth - margin - totalAmountWidth, currentY);
            
            currentY += 8;
            
            // Apply filters text if any
            const filterTexts = [];
            if (filters.order_status) filterTexts.push(`Status: ${filters.order_status}`);
            if (filters.payment_method) filterTexts.push(`Payment: ${getPaymentMethodDisplay(filters.payment_method)}`);
            if (filters.dateFrom) filterTexts.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
            if (filters.dateTo) filterTexts.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
            if (filters.order_number) filterTexts.push(`Order #: ${filters.order_number}`);
            
            if (filterTexts.length > 0) {
                doc.setFontSize(9);
                doc.setTextColor(80, 80, 80);
                const filtersText = `Filters: ${filterTexts.join(', ')}`;
                const filtersLines = splitTextIntoLines(doc, filtersText, pageWidth - 2 * margin);
                
                filtersLines.forEach(line => {
                    if (currentY > pageHeight - 40) {
                        doc.addPage('l');
                        currentY = margin;
                    }
                    doc.text(line, margin, currentY);
                    currentY += 6;
                });
                
                currentY += 10;
            }
            
            // Prepare table data
            const tableData = data.map(order => {
                const items = order.bar_order_items || [];
                const servantDisplay = items.length > 0 
                    ? [...new Set(items.map(item => item.servant_id))].length > 1
                        ? `${[...new Set(items.map(item => item.servant_id))].length} servants`
                        : `Servant #${items[0]?.servant_id || 'N/A'}`
                    : 'N/A';
                
                return [
                    formatCreatedAtDate(order.created_at),
                    order.order_number,
                    order.customer_id || 'Walk-in Customer',
                    servantDisplay,
                    items.length.toString(),
                    formatNumberWithComma(order.total_amount),
                    order.order_status?.toUpperCase() || 'N/A',
                    getPaymentMethodDisplay(order.payment_method)
                ];
            });

            // Draw the table
            drawOrdersTable(doc, tableData, currentY + 10);

            // Add page numbers
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150, 150, 150);
                doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            }

            const fileName = `bar_orders_report_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);
            resolve();
        } catch (error) {
            reject(error);
        }
    });
};